import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import {
  Spinner, EmptyState, Badge, observationRatingBadgeMap,
  useSort, SortTh, usePagination, Pagination, FilterBar, FilterSelect,
} from '../../components/UI';
import { KpiCard, SectionIcon } from '../../components/TrainerUI';
import { fmtDate } from '../../utils/format';

const RATING_SCORE = { excellent: 5, satisfactory: 3, needs_improvement: 1 };

function sessionLabel(o) {
  if (!o.session_schedule_name) return '—';
  return o.session_date ? `${o.session_schedule_name} — ${fmtDate(o.session_date)}` : o.session_schedule_name;
}

export default function TrainerObservationPage() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.get('/trainer/observations')
      .then(r => { if (!cancelled) setRows(r.data.data || []); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const q = search.trim().toLowerCase();
  const filtered = rows.filter(o =>
    (!ratingFilter || o.rating === ratingFilter) &&
    (!fromDate || o.observation_date >= fromDate) &&
    (!toDate || o.observation_date <= toDate) &&
    (!q ||
      (o.intake_code || '').toLowerCase().includes(q) ||
      (o.module_name || '').toLowerCase().includes(q) ||
      (o.module_code || '').toLowerCase().includes(q) ||
      (o.session_schedule_name || '').toLowerCase().includes(q) ||
      (o.focus_area || '').toLowerCase().includes(q) ||
      (o.observed_by_name || '').toLowerCase().includes(q) ||
      (o.remarks || '').toLowerCase().includes(q)
    )
  );
  const { sorted, sortKey, sortDir, toggleSort } = useSort(filtered, 'observation_date', 'desc');
  const { paged, page, setPage, pageSize, setPageSize, totalPages, total, start } = usePagination(sorted);
  const clearFilters = () => { setSearch(''); setRatingFilter(''); setFromDate(''); setToDate(''); };

  if (loading) return <div className="trn-page"><Spinner /></div>;

  const scored = rows.filter(r => RATING_SCORE[r.rating] !== undefined);
  const avgRating = scored.length
    ? (scored.reduce((sum, r) => sum + RATING_SCORE[r.rating], 0) / scored.length).toFixed(1)
    : null;

  return (
    <>
      <div className="trn-topbar">
        <div className="trn-topbar-title">Trainer Observation</div>
        <div className="trn-topbar-sub">Feedback and ratings from classroom observations</div>
      </div>
      <div className="trn-page">
        <div className="trn-stat-row" style={{ maxWidth: 480 }}>
          <KpiCard label="Observations (Total)" value={rows.length} icon="bi-eye" color="var(--sms-stat-violet)" />
          <KpiCard label="Average Rating" icon="bi-star" color="var(--sms-stat-amber)"
            value={avgRating !== null ? <>{avgRating}<span style={{ fontSize: 14, fontWeight: 600, color: 'var(--sms-txt2)' }}>/5</span></> : '—'} />
        </div>

        <div className="trn-card">
          <div className="trn-card-head"><h3 className="d-flex align-items-center"><SectionIcon icon="bi-clock-history" color="var(--sms-stat-blue)" />Observation History</h3></div>
          <div className="trn-card-body">
            {rows.length === 0 ? (
              <EmptyState icon="bi-eye" message="No observations recorded yet" />
            ) : (
              <>
                <FilterBar bare search={search} onSearch={setSearch}
                  searchPlaceholder="Search intake, module, session, focus area, observer, remarks…"
                  onClear={clearFilters}>
                  <FilterSelect value={ratingFilter} onChange={setRatingFilter} placeholder="All ratings"
                    options={['excellent', 'satisfactory', 'needs_improvement'].map(r => ({ value: r, label: observationRatingBadgeMap[r]?.label || r }))} />
                  <div style={{ flex: '1 1 150px', minWidth: 140 }}>
                    <input type="date" className="sms-form-control" value={fromDate} onChange={e => setFromDate(e.target.value)}
                      aria-label="From date" title="From date" />
                  </div>
                  <div style={{ flex: '1 1 150px', minWidth: 140 }}>
                    <input type="date" className="sms-form-control" value={toDate} onChange={e => setToDate(e.target.value)}
                      aria-label="To date" title="To date" min={fromDate || undefined} />
                  </div>
                </FilterBar>
                {filtered.length === 0 ? (
                  <EmptyState icon="bi-eye" message="No observations match these filters" />
                ) : (
                  <>
                    <table className="trn-table">
                      <thead>
                        <tr>
                          <SortTh label="Date" field="observation_date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                          <SortTh label="Intake" field="intake_code" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                          <SortTh label="Module" field="module_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                          <SortTh label="Session / Class" field="session_schedule_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                          <SortTh label="Observed By" field="observed_by_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                          <SortTh label="Focus Area" field="focus_area" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                          <SortTh label="Rating" field="rating" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                        </tr>
                      </thead>
                      <tbody>
                        {paged.map(o => (
                          <tr key={o.id}>
                            <td>{fmtDate(o.observation_date)}</td>
                            <td>{o.intake_code || '—'}</td>
                            <td>{o.module_name || o.module_code || '—'}</td>
                            <td>{sessionLabel(o)}</td>
                            <td>{o.observed_by_name}</td>
                            <td>{o.focus_area || '—'}</td>
                            <td><Badge value={o.rating} map={observationRatingBadgeMap} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} setPage={setPage} setPageSize={setPageSize} start={start} />
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {filtered.some(r => r.remarks) && (
          <div className="trn-card">
            <div className="trn-card-head"><h3 className="d-flex align-items-center"><SectionIcon icon="bi-chat-left-text" color="var(--sms-stat-cyan)" />Remarks</h3></div>
            <div className="trn-card-body">
              {filtered.filter(r => r.remarks).map(o => (
                <div key={o.id} style={{ marginBottom: 14 }}>
                  <div className="trn-field-label">{fmtDate(o.observation_date)} · {o.observed_by_name}</div>
                  <div className="trn-field-value" style={{ fontWeight: 400, whiteSpace: 'pre-wrap' }}>{o.remarks}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
